import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef, useState } from 'react';
import { message, Switch, Table } from '@helloworld1812/ws-components';
import { useUsersMeCached } from '@helloworld1812/hr-api';
import { Spin } from 'antd';

import { liteTierNotificationTypes } from '@/config';
import { HiringPaywallWrapper } from '@/components/tier-lite';
import { useHrMutation } from '@/api';
import type { NotificationConfig } from '@/api';
import { UserPhoneEditModal } from '@/folder-components/my-profile-page';

interface NotificationConfigsEditTableProps {
  dataSource: NotificationConfig[];
  onUpdateSuccess?: () => void;
  isFetching?: boolean;
  tableHeight?: string;
}

export function NotificationConfigsEditTable(props: NotificationConfigsEditTableProps) {
  const { user, refetch } = useUsersMeCached();
  const [waitingNotification, setWaitingNotification] = useState<NotificationConfig | null>(null);

  const { dataSource, onUpdateSuccess, isFetching, tableHeight } = props;
  const { t } = useTranslation('hr-account-routes');

  const hasPhoneNumber = !!user.phone && !!user.region_number;

  const { mutateAsync: updateNotificationConfig, isLoading } = useHrMutation({
    method: 'put',
    url: '/v1/notification_configs/{id}',
    urlParams: { id: '' },
  });

  const updateNotificationConfigSettings = async (
    notificationConfig: NotificationConfig,
    settings: Partial<NotificationConfig['settings']>
  ) => {
    try {
      await updateNotificationConfig({
        urlParams: { id: notificationConfig.id.toString() },
        data: {
          notification_config: {
            ...notificationConfig,
            settings: { ...notificationConfig.settings, ...settings },
          },
        },
      });
      message.success(t('Notification config settings updated successfully'));
      onUpdateSuccess?.();
    } catch (error) {
      message.error(t('Failed to update notification config settings'));
    }
  };

  const handleSmsToggle = (checked: boolean, data: NotificationConfig) => {
    if (!hasPhoneNumber && checked) {
      setWaitingNotification(data);
      return;
    }
    updateNotificationConfigSettings(data, { sms: checked });
  };

  const handleSmsToggleRef = useRef(handleSmsToggle);
  handleSmsToggleRef.current = handleSmsToggle;

  useEffect(() => {
    if (waitingNotification && hasPhoneNumber) {
      setWaitingNotification(null);
      handleSmsToggleRef.current(true, waitingNotification);
    }
  }, [waitingNotification, hasPhoneNumber]);

  return (
    <>
      <Table<NotificationConfig>
        loading={isFetching || isLoading}
        pagination={false}
        dataSource={dataSource}
        columns={[
          { field: 'notification_type_text', headerName: t('Event') },
          {
            field: 'settings.email',
            headerName: t('Email', { ns: 'common' }),
            cellRenderer: ({ value, data }: { value: boolean; data: NotificationConfig }) => (
              <HiringPaywallWrapper
                hasNoFunctionModule={liteTierNotificationTypes.includes(data.notification_type) && 'messaging'}
                mode="hover"
                disabledStyle="opacity"
              >
                <Switch
                  checked={value}
                  onChange={(checked) => updateNotificationConfigSettings(data, { email: checked })}
                />
              </HiringPaywallWrapper>
            ),
            cellStyle: {
              height: '100%',
              display: 'flex ',
              justifyContent: 'center',
              alignItems: 'center',
            },
            maxWidth: 100,
          },
          // TODO: add pdf_in_email when necessary
          // hasIncludePdfConfig && {
          //   field: 'settings.pdf_in_email',
          //   headerName: t('Include PDF?'),
          //   cellRenderType: 'checkbox',
          // },
          {
            field: 'settings.sms',
            headerName: t('SMS', { ns: 'common' }),
            cellRenderer: ({ value, data }: { value: boolean; data: NotificationConfig }) => (
              <HiringPaywallWrapper hasNoFunctionModule="messaging" mode="hover" disabledStyle="opacity">
                <Switch checked={value} onChange={(checked) => handleSmsToggle(checked, data)} />
              </HiringPaywallWrapper>
            ),
            cellStyle: {
              height: '100%',
              display: 'flex ',
              alignItems: 'center',
            },
            maxWidth: 100,
          },
        ]}
        loadingOverlayComponent={Spin}
        style={{ height: tableHeight ?? '500px' }}
      />
      <UserPhoneEditModal
        visible={!!waitingNotification}
        title={t('Enter your phone number to receive sms notification')}
        okText={t('Turn on', { ns: 'common' })}
        regionNumber={user.region_number ?? ''}
        phoneNumber={user.phone ?? ''}
        onCancel={() => setWaitingNotification(null)}
        onChanged={() => {
          refetch();
        }}
        isRequired
      />
    </>
  );
}
